import {
  AttributeValue,
  DynamoDBClient,
  GetItemCommand,
  PutItemCommand,
  QueryCommand,
  UpdateItemCommand,
} from '@aws-sdk/client-dynamodb'
import { marshall, unmarshall } from '@aws-sdk/util-dynamodb'
import crypto from 'crypto'
import { z } from 'zod'
import { CreateLead, Lead, LeadMagnetLead, LeadMagnetLeadCreate, leadMagnetLeadSchema, leadSchema } from './schema'
import { truncateOverLengthLeadNames } from './truncate-over-length-names'

const leadUnionSchema = z.union([
  leadMagnetLeadSchema.extend({ source: z.enum(['lead-magnet', 'advisor-check']) }),
  leadSchema,
])

export class LeadRepository {
  private client: DynamoDBClient
  private tableName = 'leads'

  constructor() {
    this.client = new DynamoDBClient({})
  }

  async create(lead: CreateLead) {
    const boundedLead = truncateOverLengthLeadNames(lead)
    const id = crypto.randomUUID()
    const createdAt = new Date().toISOString()
    const partnerSuppliedId = boundedLead.external_id ?? null
    const externalId = `${boundedLead.source}:${boundedLead.external_id ?? id}`

    const command = new PutItemCommand({
      TableName: this.tableName,
      Item: marshall({
        id,
        lead: {
          ...boundedLead,
          external_id: externalId,
          partner_supplied_id: partnerSuppliedId,
          created_at: createdAt,
        },
        email: boundedLead.email,
        phone: boundedLead.phone,
        external_id: externalId,
        created_at: createdAt,
      }),
    })

    await this.client.send(command)

    return { id }
  }

  async createFromLeadMagnet(lead: LeadMagnetLeadCreate) {
    const boundedLead = truncateOverLengthLeadNames(lead)
    const id = crypto.randomUUID()
    const createdAt = new Date().toISOString()
    const partnerSuppliedId = boundedLead.external_id ?? null
    const externalId = `${boundedLead.source}:${boundedLead.external_id ?? id}`

    const command = new PutItemCommand({
      TableName: this.tableName,
      Item: marshall({
        id,
        lead: {
          ...boundedLead,
          external_id: externalId,
          partner_supplied_id: partnerSuppliedId,
          created_at: createdAt,
        },
        email: boundedLead.email,
        external_id: externalId,
        created_at: createdAt,
      }),
    })

    await this.client.send(command)

    return { id }
  }

  async getById(id: string): Promise<Lead | LeadMagnetLead | null> {
    const command = new GetItemCommand({
      TableName: this.tableName,
      Key: marshall({ id }),
    })

    const result = await this.client.send(command)

    if (!result.Item) {
      return null
    }

    const item = unmarshall(result.Item as Record<string, AttributeValue>)

    return leadUnionSchema.parse(item.lead)
  }

  async getByEmail(email: string): Promise<(Lead | LeadMagnetLead)[]> {
    const command = new QueryCommand({
      TableName: this.tableName,
      IndexName: 'EmailIndex',
      KeyConditionExpression: 'email = :email',
      ExpressionAttributeValues: marshall({ ':email': email }),
    })

    const result = await this.client.send(command)

    if (!result.Items || result.Items.length === 0) {
      return []
    }

    return result.Items.map((rawItem) => {
      const item = unmarshall(rawItem as Record<string, AttributeValue>)
      return leadUnionSchema.parse(item.lead)
    })
  }

  async getByEmailWithDrapeStatus(email: string): Promise<{ lead: Lead | LeadMagnetLead; drapeFlagged: boolean }[]> {
    const command = new QueryCommand({
      TableName: this.tableName,
      IndexName: 'EmailIndex',
      KeyConditionExpression: 'email = :email',
      ExpressionAttributeValues: marshall({ ':email': email }),
    })

    const result = await this.client.send(command)

    if (!result.Items || result.Items.length === 0) return []

    return result.Items.map((rawItem) => {
      const item = unmarshall(rawItem as Record<string, AttributeValue>)
      const lead = leadUnionSchema.parse(item.lead)
      return { lead, drapeFlagged: !!item.drape_received_at }
    })
  }

  async getItemIdsByEmail(email: string): Promise<string[]> {
    const command = new QueryCommand({
      TableName: this.tableName,
      IndexName: 'EmailIndex',
      KeyConditionExpression: 'email = :email',
      ExpressionAttributeValues: marshall({ ':email': email }),
      ProjectionExpression: 'id',
    })

    const result = await this.client.send(command)

    if (!result.Items || result.Items.length === 0) {
      return []
    }

    return result.Items.map((rawItem) => {
      const item = unmarshall(rawItem as Record<string, AttributeValue>)
      return item.id as string
    })
  }

  async updateDrapeReceivedAt(id: string, drapeReceivedAt: string) {
    const command = new UpdateItemCommand({
      TableName: this.tableName,
      Key: marshall({ id }),
      UpdateExpression: 'SET drape_received_at = :drape_received_at',
      ExpressionAttributeValues: marshall({
        ':drape_received_at': drapeReceivedAt,
      }),
    })

    await this.client.send(command)
  }
}
